import React from 'react';
import {
  cancelCommunication,
  getAdminCommunications,
  getCommunicationMetrics,
  getCommunicationTestTemplates,
  sendCommunicationTest,
  useApiClient,
  type AdminCommunicationDto,
  type CommunicationMetricsDto,
  type CommunicationTestTemplateDto,
  type PageDto,
} from '../api/client';
import { UserTypeahead, type UserSearchResult } from '../components/UserTypeahead';
import { EmptyRow, MetricCard, PageHeader, PageShell, PaginationBar, StatusBadge } from '../components/ui';
import { formatDateTime } from '../utils/dateFormat';

const percent = (value: number) => `${(value * 100).toFixed(1)}%`;
const pageSize = 20;
const statusLabels: Record<string, string> = {
  ELIGIBLE: 'Geeignet', SUPPRESSED: 'Zurückgestellt', QUEUED: 'Eingeplant', PROCESSING: 'Wird versendet',
  ACCEPTED: 'Vom Provider angenommen', DELIVERED: 'Zugestellt', OPENED: 'Geöffnet', CLICKED: 'Link geöffnet',
  CONVERTED: 'Konvertiert', FAILED: 'Fehlgeschlagen', BOUNCED: 'Abgewiesen', CANCELLED: 'Abgebrochen',
  UNSUBSCRIBED: 'Abgemeldet', COMPLAINED: 'Als Spam gemeldet',
};
const statusLabel = (status: string) => statusLabels[status] || status;
const reasonLabels: Record<string, string> = {
  FREQUENCY_CAP_DEFERRED: 'Frequenzlimit erreicht – später erneut eingeplant',
  FREQUENCY_CAP_EXPIRED: 'Frequenzlimit erreicht – Zeitfenster abgelaufen',
  OPTED_OUT: 'Vom User deaktiviert', NO_DESTINATION: 'Keine Empfängeradresse', ADMIN_CANCELLED: 'Manuell abgebrochen',
  bounce: 'Empfängeradresse wurde abgewiesen', 'suppress-bounce': 'Adresse ist bereits als Bounce bekannt',
};
const reasonLabel = (reason: string) => reasonLabels[reason] || reason;

const statusTone = (status: string): 'slate' | 'blue' | 'green' | 'amber' | 'red' => {
  if (['DELIVERED', 'OPENED', 'CLICKED', 'CONVERTED', 'ACCEPTED'].includes(status)) return 'green';
  if (['FAILED', 'BOUNCED', 'COMPLAINED'].includes(status)) return 'red';
  if (['QUEUED', 'PROCESSING'].includes(status)) return 'blue';
  if (status === 'SUPPRESSED') return 'amber';
  return 'slate';
};

export const CommunicationDashboardPage: React.FC = () => {
  const { request } = useApiClient();
  const [days, setDays] = React.useState(30);
  const [data, setData] = React.useState<CommunicationMetricsDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [communications, setCommunications] = React.useState<PageDto<AdminCommunicationDto> | null>(null);
  const [listError, setListError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [pageNumber, setPageNumber] = React.useState(0);
  const [searchInput, setSearchInput] = React.useState('');
  const [q, setQ] = React.useState('');
  const [journey, setJourney] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [channel, setChannel] = React.useState('');
  const [provider, setProvider] = React.useState('');
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [cancellingId, setCancellingId] = React.useState<string | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [testTemplates, setTestTemplates] = React.useState<CommunicationTestTemplateDto[]>([]);
  const [recipient, setRecipient] = React.useState<UserSearchResult | null>(null);
  const [templateKey, setTemplateKey] = React.useState('');
  const [testBusy, setTestBusy] = React.useState(false);
  const [testMessage, setTestMessage] = React.useState<{ tone: 'green' | 'red'; text: string } | null>(null);

  React.useEffect(() => {
    getCommunicationTestTemplates(request)
      .then((templates) => { setTestTemplates(templates); setTemplateKey((current) => current || templates[0]?.key || ''); })
      .catch((e) => setTestMessage({ tone: 'red', text: e.message }));
  }, [request]);

  React.useEffect(() => {
    setError(null);
    getCommunicationMetrics(request, days).then(setData).catch((e) => setError(e.message));
  }, [request, days]);

  React.useEffect(() => {
    setLoading(true);
    setListError(null);
    getAdminCommunications(request, { page: pageNumber, size: pageSize, q, journey, status, channel, provider })
      .then(setCommunications)
      .catch((e) => setListError(e.message))
      .finally(() => setLoading(false));
  }, [request, pageNumber, q, journey, status, channel, provider, refreshKey]);

  const sendTest = async () => {
    if (!recipient || !templateKey) return;
    setTestBusy(true);
    setTestMessage(null);
    try {
      const result = await sendCommunicationTest(request, recipient.id, templateKey);
      setTestMessage({ tone: 'green', text: `Test ${result.status.toLowerCase()} · ${result.id}` });
      setPageNumber(0);
      setRefreshKey((value) => value + 1);
    } catch (e) {
      setTestMessage({ tone: 'red', text: e instanceof Error ? e.message : 'Testversand fehlgeschlagen.' });
    } finally {
      setTestBusy(false);
    }
  };

  const cancelQueued = async (communication: AdminCommunicationDto) => {
    if (!window.confirm(`Queue-Eintrag für ${communication.recipientEmail || communication.recipientId} wirklich abbrechen?`)) return;
    setCancellingId(communication.id);
    setListError(null);
    try {
      await cancelCommunication(request, communication.id);
      setExpandedId(null);
      setRefreshKey((value) => value + 1);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Communication konnte nicht abgebrochen werden.');
    } finally {
      setCancellingId(null);
    }
  };

  const resetPage = () => setPageNumber(0);
  const selectedTestTemplate = testTemplates.find((template) => template.key === templateKey);

  return <PageShell>
    <PageHeader title="Lifecycle-Kommunikation" description="Providerübergreifender Funnel für FCM und Mailgun. Bei Öffnungen wird zwischen erkannten Bots und regulären Clients unterschieden." actions={
      <select className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" value={days} onChange={(e) => setDays(Number(e.target.value))}>
        <option value={7}>7 Tage</option><option value={30}>30 Tage</option><option value={90}>90 Tage</option>
      </select>
    } />
    {error && <div className="rounded-lg bg-red-50 p-4 text-red-800">{error}</div>}
    {!data && !error && <div className="text-sm text-gray-500">Lade Kommunikationsmetriken…</div>}
    {data && <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <MetricCard label="Push Open Rate" value={percent(data.pushOpenRate)} tone="blue" />
        <MetricCard label="E-Mail Delivery" value={percent(data.emailDeliveryRate)} tone="green" />
        <MetricCard label="Human Open Rate" value={percent(data.humanEmailOpenRate)} tone="violet" />
        <MetricCard label="Click Rate" value={percent(data.clickRate)} tone="amber" />
        <MetricCard label="Conversion Rate" value={percent(data.conversionRate)} tone="green" />
      </div>
      <section className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-5 py-4"><h2 className="font-bold text-gray-900">Funnel</h2></div>
        <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(data.funnel).map(([key, value]) => <div key={key} className="rounded-lg bg-gray-50 p-4"><div className="text-xs font-semibold text-gray-500">{key}</div><div className="mt-1 text-2xl font-bold">{value.toLocaleString()}</div></div>)}
        </div>
      </section>
    </>}
    <section className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
        <div><h2 className="font-bold text-gray-900">Einzelne Communications</h2>
          <p className="mt-1 text-sm text-gray-500">Empfänger, Versandversuche und Event-Historie.</p></div>
        <button className="btn btn-neutral" type="button" onClick={() => setRefreshKey((value) => value + 1)}>Aktualisieren</button>
      </div>
      <form className="grid gap-3 border-b border-gray-200 p-4 md:grid-cols-3 xl:grid-cols-6" onSubmit={(event) => { event.preventDefault(); setQ(searchInput); resetPage(); }}>
        <input className="form-control" aria-label="Empfänger suchen" placeholder="E-Mail, Name oder ID" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
        <select className="form-control" aria-label="Journey" value={journey} onChange={(event) => { setJourney(event.target.value); resetPage(); }}>
          <option value="">Alle Journeys</option><option value="MANUAL_CAMPAIGN">Manuelle Kampagnen</option><option value="VERIFICATION">Verification</option><option value="RIDE_REQUEST_MATCH">Ride Request</option><option value="ROUTE_WATCH_MATCH">Route Watch</option><option value="BOOKING_STATUS">Buchungsstatus</option><option value="RIDE_CANCELLATION">Stornierungen</option><option value="RECURRING_RIDE_STATUS">Wiederkehrende Fahrten</option><option value="PAYMENT_STATUS">Payments</option><option value="DRIVER_FIRST_RIDE">First Ride</option><option value="RECURRING_ADOPTION">Recurring Adoption</option>
        </select>
        <select className="form-control" aria-label="Status" value={status} onChange={(event) => { setStatus(event.target.value); resetPage(); }}>
          <option value="">Alle Status</option>{['ELIGIBLE', 'QUEUED', 'PROCESSING', 'ACCEPTED', 'DELIVERED', 'OPENED', 'CLICKED', 'CONVERTED', 'FAILED', 'SUPPRESSED', 'CANCELLED'].map((value) => <option key={value} value={value}>{statusLabel(value)}</option>)}
        </select>
        <select className="form-control" aria-label="Kanal" value={channel} onChange={(event) => { setChannel(event.target.value); resetPage(); }}>
          <option value="">Alle Kanäle</option><option value="PUSH">Push</option><option value="EMAIL">E-Mail</option>
        </select>
        <select className="form-control" aria-label="Provider" value={provider} onChange={(event) => { setProvider(event.target.value); resetPage(); }}>
          <option value="">Alle Provider</option><option value="FCM">FCM</option><option value="MAILGUN">Mailgun</option>
        </select>
        <button className="btn btn-primary" type="submit">Suchen</button>
      </form>
      {listError && <div className="m-4 rounded-lg bg-red-50 p-4 text-red-800">{listError}</div>}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500"><tr>
            <th className="px-4 py-3">Zeit</th><th className="px-4 py-3">Empfänger</th><th className="px-4 py-3">Journey</th><th className="px-4 py-3">Route</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Versuche</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {!loading && communications?.content.length === 0 && <EmptyRow colSpan={7}>Keine Communications gefunden.</EmptyRow>}
            {loading && <EmptyRow colSpan={7}>Lade Communications…</EmptyRow>}
            {!loading && communications?.content.map((communication) => {
              const latest = communication.deliveries[0];
              const expanded = expandedId === communication.id;
              return <React.Fragment key={communication.id}>
                <tr className="align-top">
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600">{formatDateTime(communication.createdAt)}</td>
                  <td className="px-4 py-3"><div className="font-semibold text-gray-900">{communication.recipientName || '—'}</div><div>{communication.recipientEmail}</div><div className="font-mono text-xs text-gray-400">{communication.recipientId}</div></td>
                  <td className="px-4 py-3"><div className="font-semibold">{communication.journey}</div><div className="text-xs text-gray-500">{communication.variant || '—'}</div></td>
                  <td className="px-4 py-3">{communication.channel} · {communication.provider}</td>
                  <td className="px-4 py-3"><StatusBadge tone={statusTone(communication.status)}>{statusLabel(communication.status)}</StatusBadge>{communication.suppressionReason && <div className="mt-1 text-xs text-amber-700">{communication.suppressionReason}</div>}</td>
                  <td className="px-4 py-3"><div>{communication.deliveries.length}</div>{latest?.errorMessage && <div className="mt-1 max-w-xs text-xs text-red-700">{latest.errorMessage}</div>}</td>
                  <td className="px-4 py-3"><div className="flex gap-2">
                    <button className="btn btn-neutral" type="button" aria-expanded={expanded} onClick={() => setExpandedId(expanded ? null : communication.id)}>{expanded ? 'Schließen' : 'Details'}</button>
                    {communication.status === 'QUEUED' && <button className="btn btn-danger" type="button" disabled={cancellingId === communication.id} onClick={() => cancelQueued(communication)}>{cancellingId === communication.id ? 'Bricht ab…' : 'Abbrechen'}</button>}
                  </div></td>
                </tr>
                {expanded && <tr><td colSpan={7} className="bg-gray-50 px-5 py-5"><CommunicationDetails communication={communication} /></td></tr>}
              </React.Fragment>;
            })}
          </tbody>
        </table>
      </div>
      <PaginationBar loading={loading} pageNumber={pageNumber} pageSize={pageSize} rowCount={communications?.content.length ?? 0} totalElements={communications?.totalElements} isLast={communications?.last} onPrevious={() => setPageNumber((value) => Math.max(0, value - 1))} onNext={() => setPageNumber((value) => value + 1)} previousLabel="Zurück" nextLabel="Weiter" />
    </section>
    <section className="mt-6 overflow-visible rounded-xl border border-purple-200 bg-white shadow-sm">
      <div className="rounded-t-xl border-b border-purple-100 bg-purple-50 px-5 py-4">
        <h2 className="font-bold text-gray-900">Lifecycle testen</h2>
        <p className="mt-1 text-sm text-gray-600">Schickt eine echte Test-Communication über den gewählten Provider. User-Opt-outs und Journey-Limits werden für diesen Admin-Test ignoriert; Testsendungen zählen nicht in die KPIs.</p>
      </div>
      <div className="grid gap-4 p-5 lg:grid-cols-3">
        <div>
          <label className="block text-sm font-semibold text-gray-700">
            <span>User suchen</span>
            <div className="mt-1 font-normal">
              <UserTypeahead onSelect={(user) => { setRecipient(user); setTestMessage(null); }} placeholder="E-Mail oder Name eingeben" />
            </div>
          </label>
          {recipient && <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-purple-100 bg-purple-50 px-3 py-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-gray-900">{[recipient.firstName, recipient.lastName].filter(Boolean).join(' ') || recipient.email}</div>
              <div className="truncate text-xs text-gray-500">{recipient.email}</div>
            </div>
            <button className="btn btn-ghost shrink-0 px-2 py-1 text-xs" type="button" onClick={() => setRecipient(null)}>Entfernen</button>
          </div>}
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700" htmlFor="test-template">Journey-Template</label>
          <select id="test-template" className="form-control mt-1" value={templateKey} onChange={(event) => setTemplateKey(event.target.value)}>
            {testTemplates.map((template) => <option key={template.key} value={template.key} disabled={!template.configured}>{template.label} · {template.provider}{template.configured ? '' : ' (nicht konfiguriert)'}</option>)}
          </select>
          {selectedTestTemplate && <div className="mt-2 text-xs text-gray-500">
            <div>{selectedTestTemplate.channel} über {selectedTestTemplate.provider}</div>
            <div className="mt-1 font-semibold text-gray-700">{selectedTestTemplate.subject || selectedTestTemplate.title}</div>
            <div>{selectedTestTemplate.body || (selectedTestTemplate.channel === 'EMAIL' ? 'HTML wird im Backend gerendert.' : '')}</div>
          </div>}
        </div>
        <div className="flex flex-col justify-end">
          <button className="btn btn-primary" type="button" disabled={testBusy || !recipient || !templateKey} onClick={sendTest}>{testBusy ? 'Wird gesendet…' : 'Test senden'}</button>
          {testMessage && <div className={`mt-2 break-all rounded-lg px-3 py-2 text-sm ${testMessage.tone === 'green' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>{testMessage.text}</div>}
        </div>
      </div>
    </section>
  </PageShell>;
};

function CommunicationDetails({ communication }: { communication: AdminCommunicationDto }) {
  const heading = communication.subject || communication.title || communication.templateId || 'Nachricht ohne Titel';
  const reference = [communication.referenceType, communication.referenceId].filter(Boolean).join(' · ') || '—';
  const events = communication.events.filter((event, index, all) =>
    event.type !== 'ACCEPTED' || index === all.findIndex((candidate) => candidate.type === 'ACCEPTED'));

  return <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm" role="region" aria-label={`Details zu ${heading}`}>
    <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{communication.category} · {communication.journey}</div>
        <h3 className="mt-1 break-words text-lg font-bold text-gray-900">{heading}</h3>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
          <span>{communication.channel}</span><span aria-hidden="true">•</span><span>{communication.provider}</span>
          {communication.variant && <><span aria-hidden="true">•</span><span>Variante {communication.variant}</span></>}
        </div>
      </div>
      <StatusBadge tone={statusTone(communication.status)} className="shrink-0">{statusLabel(communication.status)}</StatusBadge>
    </div>

    <dl className="grid bg-gray-50/70 sm:grid-cols-2 xl:grid-cols-4">
      <DetailItem label="Erstellt" value={formatDateTime(communication.createdAt)} />
      <DetailItem label="Geplant" value={formatDateTime(communication.scheduledAt)} />
      <DetailItem label="Aktualisiert" value={formatDateTime(communication.updatedAt)} />
      <DetailItem label="Referenz" value={reference} mono={reference !== '—'} />
    </dl>

    <div className="grid lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
      <div className="min-w-0 space-y-6 p-5 lg:border-r lg:border-gray-200">
        <section>
          <h4 className="text-sm font-semibold text-gray-900">Nachricht</h4>
          <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
            {communication.body
              ? <p className="whitespace-pre-wrap break-words leading-6">{communication.body}</p>
              : <p className="italic text-gray-500">Kein Nachrichtentext gespeichert.</p>}
          </div>
          <div className="mt-2 break-all font-mono text-xs text-gray-400">Communication-ID: {communication.id}</div>
        </section>

        <section>
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold text-gray-900">Zustellversuche</h4>
            <span className="text-xs text-gray-500">{communication.deliveries.length}</span>
          </div>
          <div className="mt-2 space-y-2">
            {communication.deliveries.length === 0 && <div className="rounded-lg border border-dashed border-gray-300 px-4 py-5 text-center text-sm text-gray-500">Noch keine Zustellversuche.</div>}
            {communication.deliveries.map((delivery) => <div key={delivery.id} className="rounded-lg border border-gray-200 px-4 py-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">Versuch {delivery.attemptNumber}</span>
                  <StatusBadge tone={statusTone(delivery.status)}>{statusLabel(delivery.status)}</StatusBadge>
                </div>
                <time className="text-xs text-gray-500">{formatDateTime(delivery.attemptedAt)}</time>
              </div>
              {delivery.errorMessage && <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
                {delivery.errorCode && <div className="mb-0.5 font-mono text-xs font-semibold">{delivery.errorCode}</div>}
                <div className="break-words">{delivery.errorMessage}</div>
              </div>}
              {delivery.providerMessageId && <details className="mt-2 text-xs text-gray-400">
                <summary className="cursor-pointer select-none">Technische Details</summary>
                <div className="mt-1 break-all font-mono">Provider-ID: {delivery.providerMessageId}</div>
              </details>}
            </div>)}
          </div>
        </section>
      </div>

      <section className="min-w-0 p-5">
        <div className="flex items-center justify-between gap-3">
          <h4 className="text-sm font-semibold text-gray-900">Event-Historie</h4>
          <span className="text-xs text-gray-500">{events.length}</span>
        </div>
        {events.length === 0
          ? <div className="mt-2 rounded-lg border border-dashed border-gray-300 px-4 py-5 text-center text-sm text-gray-500">Noch keine Events.</div>
          : <ol className="mt-4 space-y-0">
            {events.map((event, index) => <li key={`${event.type}-${event.occurredAt}-${index}`} className="relative border-l border-gray-200 pb-5 pl-5 last:border-transparent last:pb-0">
              <span className="absolute -left-1.5 top-1 h-3 w-3 rounded-full border-2 border-white bg-secondary ring-1 ring-purple-200" aria-hidden="true" />
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-gray-900">{statusLabel(event.type)}</span>
                {['OPENED', 'CLICKED'].includes(event.type) && event.human != null && <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{event.human ? 'Nicht als Bot erkannt' : 'Automatisiert erkannt'}</span>}
              </div>
              <time className="mt-1 block text-xs text-gray-500">{formatDateTime(event.occurredAt)}</time>
              {['FAILED', 'BOUNCED'].includes(event.type) && (event.errorMessage || event.reason) && <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
                <div className="mb-0.5 flex flex-wrap gap-2 font-mono text-xs font-semibold">
                  {event.errorCode && <span>{event.errorCode}</span>}
                  {event.severity && <span>{event.severity === 'permanent' ? 'Permanent' : event.severity === 'temporary' ? 'Temporär' : event.severity}</span>}
                </div>
                <div className="break-words">{event.errorMessage || reasonLabel(event.reason!)}</div>
              </div>}
              {event.reason && !['FAILED', 'BOUNCED'].includes(event.type) && <div className="mt-1 text-xs text-gray-600">{reasonLabel(event.reason)}</div>}
              {event.providerEventId && <details className="mt-1 text-xs text-gray-400">
                <summary className="cursor-pointer select-none">Technische ID</summary>
                <div className="mt-1 break-all font-mono">{event.providerEventId}</div>
              </details>}
            </li>)}
          </ol>}
      </section>
    </div>
  </div>;
}

function DetailItem({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return <div className="min-w-0 border-b border-gray-200 px-5 py-3 last:border-b-0 sm:border-r sm:last:border-r-0 xl:border-b-0">
    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className={`mt-1 break-words text-sm text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
  </div>;
}
